/* ==========================================================================
   Plant Doctor — behavior
   No dependencies. Scoped to the .pd-wrapper instance(s) on the page.
   Loaded with `defer`, so the DOM is already parsed when this runs.
   ========================================================================== */

(function () {
  'use strict';

  // ---- CONFIG: fill these in ----------------------------------------------
  var CLOUDINARY_CLOUD_NAME = 'ni1wymzc';              // from Cloudinary dashboard
  var CLOUDINARY_UPLOAD_PRESET = 'Plant Doctor';       // your unsigned upload preset name
  var AIRTABLE_PROXY_URL = 'https://hello-world.socalsoulplants.workers.dev'; // your Cloudflare Worker URL
  // --------------------------------------------------------------------------

  var wrappers = document.querySelectorAll('.pd-wrapper');
  if (!wrappers.length) return;

  wrappers.forEach(initPlantDoctor);

  function initPlantDoctor(wrapper) {
    var form = wrapper.querySelector('form');
    if (!form) return;

    initCardReveal(wrapper);
    initProgressBar(wrapper);
    initOtherSymptomToggle(form);
    initCharCounter(form);
    var photoUpload = initPhotoUpload(form);
    initValidation(form, photoUpload);
  }

  /* ---------------------------------------------------------------------
     Cards fade/slide in as they enter the viewport.
     --------------------------------------------------------------------- */
  function initCardReveal(wrapper) {
    var cards = wrapper.querySelectorAll('[data-pd-card]');
    if (!cards.length) return;

    if (!('IntersectionObserver' in window)) {
      cards.forEach(function (card) { card.classList.add('pd-card-visible'); });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('pd-card-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );

    cards.forEach(function (card) { observer.observe(card); });
  }

  /* ---------------------------------------------------------------------
     Sticky progress bar reflects how far through the form the user is.
     --------------------------------------------------------------------- */
  function initProgressBar(wrapper) {
    var bar = wrapper.querySelector('[data-pd-progress-bar]');
    if (!bar) return;

    var ticking = false;

    function update() {
      var rect = wrapper.getBoundingClientRect();
      var total = wrapper.offsetHeight - window.innerHeight;
      var scrolled = -rect.top;
      var pct = total > 0 ? Math.min(Math.max(scrolled / total, 0), 1) : 0;
      bar.style.width = (pct * 100) + '%';
      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();
  }

  /* ---------------------------------------------------------------------
     "Other" symptom checkbox reveals a free-text field.
     --------------------------------------------------------------------- */
  function initOtherSymptomToggle(form) {
    var toggle = form.querySelector('[data-pd-other-toggle]');
    var field = form.querySelector('[data-pd-other-field]');
    if (!toggle || !field) return;

    toggle.addEventListener('change', function () {
      field.hidden = !toggle.checked;
      if (toggle.checked) {
        var input = field.querySelector('input, textarea');
        if (input) input.focus();
      }
    });
  }

  /* ---------------------------------------------------------------------
     Live character counter for the notes textarea.
     --------------------------------------------------------------------- */
  function initCharCounter(form) {
    var textarea = form.querySelector('[data-pd-counter]');
    var output = form.querySelector('[data-pd-counter-current]');
    if (!textarea || !output) return;

    function update() {
      output.textContent = textarea.value.length;
    }

    textarea.addEventListener('input', update);
    update();
  }

  /* ---------------------------------------------------------------------
     Photo upload: drag-and-drop / click-to-browse uploader that sends
     files straight to Cloudinary (unsigned preset, no backend needed)
     and writes the resulting URLs into the three hidden
     contact[Photo N URL] fields so they ride along with the normal
     Shopify form submission.
     --------------------------------------------------------------------- */
  function initPhotoUpload(form) {
    var MAX_PHOTOS = 5;
    var MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

    var dropzone = form.querySelector('[data-pd-dropzone]');
    var input = form.querySelector('[data-pd-photo-input]');
    var grid = form.querySelector('[data-pd-photo-grid]');
    var errorEl = form.querySelector('[data-pd-photo-error]');
    var hiddenFields = Array.prototype.slice.call(form.querySelectorAll('[data-pd-photo-field]'));

    if (!dropzone || !input || !grid) {
      return { isUploading: function () { return false; }, getPhotoUrls: function () { return []; } };
    }

    var slots = []; // { id, file, previewUrl, status: 'uploading'|'done'|'error', progress, url, errorMessage }
    var uploadingCount = 0;

    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg;
      errorEl.classList.add('pd-field-error-visible');
    }

    function clearError() {
      if (!errorEl) return;
      errorEl.classList.remove('pd-field-error-visible');
    }

    function syncHiddenFields() {
      var urls = slots
        .filter(function (s) { return s.status === 'done'; })
        .map(function (s) { return s.url; });
      hiddenFields.forEach(function (field, i) {
        field.value = urls[i] || '';
      });
    }

    function renderSlot(slot) {
      var card = document.createElement('div');
      card.className = 'pd-photo-card';
      card.dataset.id = slot.id;

      var thumb = document.createElement('div');
      thumb.className = 'pd-photo-thumb';
      var img = document.createElement('img');
      img.src = slot.previewUrl;
      img.alt = slot.file.name;
      thumb.appendChild(img);

      if (slot.status === 'uploading') {
        var overlay = document.createElement('div');
        overlay.className = 'pd-photo-overlay';
        var bar = document.createElement('div');
        bar.className = 'pd-photo-progress';
        var barFill = document.createElement('div');
        barFill.className = 'pd-photo-progress-fill';
        barFill.style.width = (slot.progress || 0) + '%';
        bar.appendChild(barFill);
        overlay.appendChild(bar);
        thumb.appendChild(overlay);
      }

      if (slot.status === 'error') {
        thumb.classList.add('pd-photo-thumb-error');
      }

      var remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'pd-photo-remove';
      remove.setAttribute('aria-label', 'Remove photo');
      remove.textContent = '\u00D7';
      remove.addEventListener('click', function () {
        removeSlot(slot.id);
      });

      card.appendChild(thumb);
      card.appendChild(remove);

      if (slot.status === 'error') {
        var errText = document.createElement('p');
        errText.className = 'pd-photo-card-error';
        errText.textContent = slot.errorMessage || 'Upload failed';
        card.appendChild(errText);
      }

      return card;
    }

    function render() {
      grid.innerHTML = '';
      slots.forEach(function (slot) {
        grid.appendChild(renderSlot(slot));
      });
    }

    function removeSlot(id) {
      slots = slots.filter(function (s) { return s.id !== id; });
      render();
      syncHiddenFields();
      clearError();
    }

    function uploadFile(slot) {
      uploadingCount++;
      var xhr = new XMLHttpRequest();
      var url = 'https://api.cloudinary.com/v1_1/' + CLOUDINARY_CLOUD_NAME + '/image/upload';
      var formData = new FormData();
      formData.append('file', slot.file);
      formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);

      xhr.upload.addEventListener('progress', function (e) {
        if (e.lengthComputable) {
          slot.progress = Math.round((e.loaded / e.total) * 100);
          render();
        }
      });

      xhr.addEventListener('load', function () {
        uploadingCount--;
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            var data = JSON.parse(xhr.responseText);
            slot.status = 'done';
            slot.url = data.secure_url;
          } catch (err) {
            slot.status = 'error';
            slot.errorMessage = 'Upload failed';
          }
        } else {
          slot.status = 'error';
          slot.errorMessage = 'Upload failed';
        }
        render();
        syncHiddenFields();
      });

      xhr.addEventListener('error', function () {
        uploadingCount--;
        slot.status = 'error';
        slot.errorMessage = 'Upload failed \u2014 check your connection';
        render();
      });

      xhr.open('POST', url);
      xhr.send(formData);
    }

    function handleFiles(fileList) {
      clearError();
      var files = Array.prototype.slice.call(fileList);
      var availableSlots = MAX_PHOTOS - slots.length;

      if (files.length > availableSlots) {
        showError('You can upload up to ' + MAX_PHOTOS + ' photos. Only the first ' + availableSlots + ' were added.');
        files = files.slice(0, availableSlots);
      }

      files.forEach(function (file) {
        if (!file.type || file.type.indexOf('image/') !== 0) {
          showError('Only image files are supported.');
          return;
        }
        if (file.size > MAX_FILE_SIZE) {
          showError('Photos must be under 10MB each.');
          return;
        }

        var slot = {
          id: 'p' + Date.now() + Math.random().toString(36).slice(2),
          file: file,
          previewUrl: URL.createObjectURL(file),
          status: 'uploading',
          progress: 0
        };
        slots.push(slot);
        render();
        uploadFile(slot);
      });
    }

    input.addEventListener('change', function () {
      handleFiles(input.files);
      input.value = '';
    });

    dropzone.addEventListener('click', function () {
      input.click();
    });

    dropzone.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        input.click();
      }
    });

    ['dragenter', 'dragover'].forEach(function (evt) {
      dropzone.addEventListener(evt, function (e) {
        e.preventDefault();
        dropzone.classList.add('pd-dropzone-active');
      });
    });

    ['dragleave', 'drop'].forEach(function (evt) {
      dropzone.addEventListener(evt, function (e) {
        e.preventDefault();
        dropzone.classList.remove('pd-dropzone-active');
      });
    });

    dropzone.addEventListener('drop', function (e) {
      if (e.dataTransfer && e.dataTransfer.files) {
        handleFiles(e.dataTransfer.files);
      }
    });

    return {
      isUploading: function () {
        return uploadingCount > 0;
      },
      getPhotoUrls: function () {
        return slots
          .filter(function (s) { return s.status === 'done'; })
          .map(function (s) { return s.url; });
      }
    };
  }

  /* ---------------------------------------------------------------------
     Airtable submission: fires alongside (not instead of) the native
     Shopify form POST, so the email notification keeps working exactly
     as before even if this request fails for any reason.
     --------------------------------------------------------------------- */
  function buildAirtablePayload(form, photoUrls) {
    var get = function (name) {
      var el = form.querySelector('[name="' + name + '"]');
      return el ? el.value : '';
    };

    var symptoms = Array.prototype.slice
      .call(form.querySelectorAll('[data-pd-symptom-group] input[type="checkbox"]:checked'))
      .map(function (box) {
        var label = box.closest('label');
        return label ? label.textContent.trim() : box.name;
      });

    return {
      submissionId: 'PD-' + Date.now().toString(36).toUpperCase(),
      website: get('website'), // honeypot — should always be empty for real users
      name: get('contact[name]'),
      email: get('contact[email]'),
      phone: get('contact[Phone]'),
      contactMethod: get('contact[Preferred Contact Method]'),
      plantName: get('contact[Plant Name]'),
      howLongOwned: get('contact[How Long Owned]'),
      wherePurchased: get('contact[Where Purchased]'),
      symptoms: symptoms,
      symptomOther: get('contact[Symptom - Other]'),
      affectedPart: get('contact[Affected Plant Part]'),
      symptomOnset: get('contact[Symptom Onset]'),
      symptomTrend: get('contact[Symptom Trend]'),
      windowDirection: get('contact[Window Direction]'),
      growLight: get('contact[Grow Light]'),
      lightLevel: get('contact[Light Level]'),
      wateringFrequency: get('contact[Watering Frequency]'),
      waterType: get('contact[Water Type]'),
      wateringMethod: get('contact[Watering Method]'),
      drainage: get('contact[Drainage]'),
      drainSpeed: get('contact[Drain Speed]'),
      lastRepotted: get('contact[Last Repotted]'),
      potType: get('contact[Pot Type]'),
      soilType: get('contact[Soil Type]'),
      potDiameter: get('contact[Pot Diameter]'),
      potHeight: get('contact[Pot Height]'),
      humidityLevel: get('contact[Humidity Level]'),
      nearDraft: get('contact[Near Draft or Vent]'),
      fertilizing: get('contact[Fertilizing Routine]'),
      recentChanges: get('contact[Recent Changes]'),
      notes: get('contact[body]'),
      photoUrls: photoUrls || []
    };
  }

  function submitToWorker(form, photoUrls) {
    var payload = buildAirtablePayload(form, photoUrls);
    return fetch(AIRTABLE_PROXY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (res) {
      if (!res.ok) throw new Error('Request failed');
      return res.json();
    });
  }

  /* ---------------------------------------------------------------------
     Validation: native required fields + the symptom checkbox group
     (which can't use the `required` attribute since it's several
     independently-named checkboxes), plus a check that no photo is
     still mid-upload. Submission is only blocked when something is
     actually invalid; otherwise the native Shopify form POST proceeds
     untouched, and the Airtable copy fires alongside it.
     --------------------------------------------------------------------- */
  function initValidation(form, photoUpload) {
    var submitBtn = form.querySelector('[data-pd-submit]');
    var submitLabel = form.querySelector('[data-pd-submit-label]');
    var symptomGroup = form.querySelector('[data-pd-symptom-group]');
    var symptomError = form.querySelector('[data-pd-symptom-error]');
    var photoError = form.querySelector('[data-pd-photo-error]');
    var successEl = document.querySelector('[data-pd-success]');
    var submitErrorEl = document.querySelector('[data-pd-submit-error]');

    var requiredFields = Array.prototype.slice.call(
      form.querySelectorAll('input[required], select[required], textarea[required]')
    );

    requiredFields.forEach(function (field) {
      field.addEventListener('input', function () { clearInvalid(field); });
      field.addEventListener('change', function () { clearInvalid(field); });
    });

    function clearInvalid(field) {
      if (field.value && field.value.trim() !== '') {
        field.classList.remove('pd-invalid');
      }
    }

    function symptomsSelected() {
      if (!symptomGroup) return true;
      var boxes = symptomGroup.querySelectorAll('input[type="checkbox"]');
      return Array.prototype.some.call(boxes, function (box) { return box.checked; });
    }

    if (symptomGroup) {
      symptomGroup.addEventListener('change', function () {
        if (symptomsSelected()) {
          symptomGroup.classList.remove('pd-invalid');
          if (symptomError) symptomError.classList.remove('pd-field-error-visible');
        }
      });
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault(); // this form no longer posts to Shopify natively

      if (submitErrorEl) submitErrorEl.hidden = true;

      // Block submission while a photo is still mid-upload.
      if (photoUpload && photoUpload.isUploading()) {
        if (photoError) {
          photoError.textContent = 'Please wait for your photos to finish uploading.';
          photoError.classList.add('pd-field-error-visible');
          photoError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }

      var firstInvalid = null;

      requiredFields.forEach(function (field) {
        var valid = field.value && field.value.trim() !== '';
        if (!valid) {
          field.classList.add('pd-invalid');
          if (!firstInvalid) firstInvalid = field;
        } else {
          field.classList.remove('pd-invalid');
        }
      });

      if (!symptomsSelected()) {
        if (symptomError) symptomError.classList.add('pd-field-error-visible');
        if (!firstInvalid) firstInvalid = symptomGroup;
      } else if (symptomError) {
        symptomError.classList.remove('pd-field-error-visible');
      }

      if (firstInvalid) {
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        var focusable = firstInvalid.matches('input, select, textarea')
          ? firstInvalid
          : firstInvalid.querySelector('input, select, textarea');
        if (focusable) focusable.focus({ preventScroll: true });
        return;
      }

      // Everything checks out — submit to the Worker (Airtable + email),
      // then show a custom success or error state.
      var photoUrls = photoUpload ? photoUpload.getPhotoUrls() : [];

      if (submitBtn) {
        submitBtn.disabled = true;
        if (submitLabel) submitLabel.textContent = 'Sending\u2026';
      }

      submitToWorker(form, photoUrls)
        .then(function () {
          form.hidden = true;
          if (successEl) {
            successEl.hidden = false;
            successEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        })
        .catch(function () {
          if (submitErrorEl) {
            submitErrorEl.hidden = false;
            submitErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          if (submitBtn) {
            submitBtn.disabled = false;
            if (submitLabel) submitLabel.textContent = 'Submit Plant Doctor Request';
          }
        });
    });
  }
})();